<template>
  <div class="time-slider-container">
    <div class="slider-track">
      <div class="track-fill" :style="{ width: fillWidth }"></div>

      <div
        v-for="(hour, index) in hours"
        :key="index"
        class="hour-marker"
        :class="{ active: currentValue >= hour.value }"
        :style="{ left: (index / (hours.length - 1)) * 100 + '%' }"
      >
        <div class="marker-dot"></div>
        <div class="hour-label medium-l font-family-2 t-grey">
          {{ hour.label }}
        </div>
      </div>
    </div>

    <input
      type="range"
      class="slider-input"
      :min="0"
      :max="hours.length - 1"
      :step="1"
      v-model="sliderIndex"
      @input="updateValue"
    />

    <div
      class="slider-thumb"
      :style="{ left: calculateThumbPosition(currentValue) }"
    >
      <div class="thumb-icon">
        <img
          src="/images/slider-thumb-2.webp"
          class="thumb-icon-image"
          alt="thumb-icon-image"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";

// Props with default values
const props = defineProps({
  initialValue: {
    type: Number,
    default: 1,
  },
});

// Emits
const emit = defineEmits(["update:value"]);

// Available hour values
const hours = [
  { value: 1, label: "1h" },
  { value: 2, label: "2h" },
  { value: 3, label: "3h" },
  { value: 4, label: "4h" },
  { value: 6, label: "6h" },
  { value: 8, label: "8h" },
  { value: 10, label: "10h" },
];

// Current value and slider index
const currentValue = ref(props.initialValue);
const sliderIndex = ref(0);

// Initialize sliderIndex based on initialValue
onMounted(() => {
  const initialIndex = hours.findIndex(
    (hour) => hour.value === props.initialValue
  );
  sliderIndex.value = initialIndex >= 0 ? initialIndex : 0;
  currentValue.value = hours[sliderIndex.value].value;
  updateValue();
});

// Update currentValue when sliderIndex changes
watch(sliderIndex, (newIndex) => {
  currentValue.value = hours[newIndex].value;
});

const fillWidth = computed(() => {
  return (sliderIndex.value / (hours.length - 1)) * 100 + "%";
});

// Calculate thumb position based on current slider index
const calculateThumbPosition = () => {
  const position = (sliderIndex.value / (hours.length - 1)) * 100;
  if (position <= 0) return `3%`;
  if (position >= 100) return `97%`;
  if (position >= 50) {
    return position + "%";
  }
  return position + 2 + "%";
};

// Update the value and emit event
const updateValue = () => {
  emit("update:value", currentValue.value);
};
</script>

<style lang="scss" scoped>
.time-slider-container {
  position: relative;
  width: 100%;
  height: 80px;
  padding: 20px 0;

  .slider-track {
    position: relative;
    width: 100%;
    height: 6px;
    background: var(--Color-Elements-Dark-1, #141416);
    border-radius: 3px;
    overflow: visible;

    .track-fill {
      position: absolute;
      height: 100%;
      background-color: var(--orange);
      border-radius: 3px;
      left: 0;
      top: 0;
    }

    .hour-marker {
      position: absolute;
      transform: translateX(-50%);

      .marker-dot {
        width: 10px;
        height: 10px;
        background-color: var(--Color-Elements-Dark-3, #242428);
        border-radius: 50%;
        margin-bottom: 8px;
        border: 2px solid var(--Color-Elements-Dark-1, #141416);
        transform: translateY(-1.5px);
        transition: background-color 0.3s ease;
      }

      &.active .marker-dot {
        background-color: #f6a54c;
        border: 2px solid var(--bg-main);
      }

      .hour-label {
        transform: translateX(-50%);
        position: absolute;
        left: 50%;
        margin-top: 16px;
        white-space: nowrap;
      }
    }
  }

  .slider-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 40px;
    opacity: 0;
    cursor: pointer;
    z-index: 10;
  }

  .slider-thumb {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -100%);
    z-index: 5;

    .thumb-icon {
      width: 42px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      color: white;

      .thumb-icon-image {
        width: 42px;
        height: 30px;
      }
    }
  }
}

.time-slider-container {
  border-radius: 6px;
  padding: 20px;
  margin: 10px 0;
}
</style>
